import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOrganizationId } from '@/hooks/useSwr';
import { createEmptyOutput } from '@/utils/create-empty-output';

import { fetcher } from '../../data/fetcher';
import { useLanguagePreference } from '../../hooks/useLanguagePreferenceContext/useLanguagePreference';
import {
  DatasetDataType,
  ExampleSchema,
  RepoExampleCreateSchema,
} from '../../types/schema';
import {
  apiExamplesPath,
  appDatasetsPath,
  appOrganizationPath,
  getExamplesMultiPartPath,
} from '../../utils/constants';
import { JsonSchema7 } from '../SchemaEditor/JsonSchema';
import { isSimpleParamsEditorAvailable } from './utils';

interface ExampleUploadWithAttachments {
  id: string;
  dataset_id: string;
  inputs?: string;
  outputs?: string;
  metadata?: string;
  split?: string[];
  attachments?: Record<string, File>;
  attachments_operations?: {
    retain: string[];
    rename: Record<string, string>;
  };
}

interface MultipartResponse {
  count?: number;
  example_ids?: string[];
}

export function useExamplesMultipart({
  handleClose,
  onSuccess,
  onError,
}: {
  handleClose?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) {
  const organizationId = useOrganizationId();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submitExamples = async (
    examples: ExampleUploadWithAttachments[],
    method: 'POST' | 'PATCH' = 'POST'
  ) => {
    const formData = new FormData();
    const datasetId = examples[0].dataset_id ?? undefined;
    try {
      examples.forEach((example) => {
        if (example.dataset_id !== datasetId) {
          throw new Error('All examples must belong to the same dataset');
        }
        const parsedInputs = example.inputs
          ? JSON.parse(example.inputs)
          : undefined;
        const parsedOutputs = example.outputs
          ? JSON.parse(example.outputs)
          : undefined;
        const parsedMetadata = example.metadata
          ? JSON.parse(example.metadata)
          : undefined;
        const parsedSplit = example.split
          ? JSON.parse(JSON.stringify(example.split))
          : undefined;
        const parsedAttachmentsOperations = example.attachments_operations
          ? JSON.parse(JSON.stringify(example.attachments_operations))
          : undefined;

        if (
          parsedInputs &&
          (typeof parsedInputs !== 'object' || Array.isArray(parsedInputs))
        ) {
          throw new Error('Invalid JSON');
        }
        if (
          parsedOutputs &&
          (typeof parsedOutputs !== 'object' || Array.isArray(parsedOutputs))
        ) {
          throw new Error('Invalid JSON');
        }

        const exampleBody = {
          ...(parsedMetadata && { metadata: parsedMetadata }),
          ...(parsedSplit && { split: parsedSplit }),
        };

        const exampleId = example.id;

        formData.append(
          exampleId,
          new Blob([JSON.stringify(exampleBody)], { type: 'application/json' })
        );

        formData.append(
          `${exampleId}.inputs`,
          new Blob([JSON.stringify(parsedInputs)], { type: 'application/json' })
        );

        if (parsedOutputs) {
          formData.append(
            `${exampleId}.outputs`,
            new Blob([JSON.stringify(parsedOutputs)], {
              type: 'application/json',
            })
          );
        }

        if (example.attachments) {
          Object.entries(example.attachments).forEach(([name, file]) => {
            // Are all names allowed?
            const blob = new Blob([file], {
              type: `${file.type};length=${file.size}`,
            });
            formData.append(`${exampleId}.attachment.${name}`, blob, file.name);
          });
        }

        if (parsedAttachmentsOperations) {
          formData.append(
            `${exampleId}.attachments_operations`,
            new Blob([JSON.stringify(parsedAttachmentsOperations)], {
              type: 'application/json',
            })
          );
        }
      });
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Something went wrong';
      setError(errorMessage);
      return;
    }

    setLoading(true);
    try {
      const response = await fetcher<MultipartResponse>({
        url: getExamplesMultiPartPath(datasetId),
        method: method,
        body: formData,
        headers: {
          'X-Tenant-Id': organizationId,
        },
      });
      if (response !== undefined && response.count !== undefined) {
        setError(null);
        handleClose?.();
        onSuccess?.();
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Something went wrong';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    submitExamples,
    loading,
    error,
    setError,
  };
}

export const mutateExample = async (
  {
    organizationId,
    example,
    dataType: datasetDataType,
  }: {
    organizationId?: string | null | undefined;
    example?: ExampleSchema | undefined;
    dataType: DatasetDataType;
  },
  {
    dataset_id,
    inputs,
    outputs,
    metadata,
    source_run_id,
    use_source_run_attachments,
    attachments_operations,
    split,
  }: {
    dataset_id: string;
    inputs?: string;
    outputs?: string;
    metadata?: string;
    source_run_id?: string;
    use_source_run_attachments?: string[];
    attachments_operations?: {
      retain: string[];
      rename: Record<string, string>;
    };
    split?: string[];
  },
  resolveWithResponse?: boolean
) => {
  let payload: string;
  try {
    const parsedInputs = inputs ? JSON.parse(inputs) : undefined;
    let parsedOutputs = outputs ? JSON.parse(outputs) : undefined;
    const parsedMetadata = metadata ? JSON.parse(metadata) : undefined;

    //if outputs is empty, set to an empty output so that the backend will still accept it
    if (
      (parsedOutputs == null ||
        (Object.keys(parsedOutputs).length === 0 &&
          datasetDataType !== 'kv')) &&
      outputs != null
    ) {
      parsedOutputs = createEmptyOutput(datasetDataType);
    }

    payload = JSON.stringify({
      dataset_id,
      inputs: parsedInputs,
      outputs: parsedOutputs,
      metadata: parsedMetadata,
      source_run_id,
      use_source_run_attachments,
      attachments_operations,
      split,
    });
    if (
      parsedInputs &&
      (typeof parsedInputs !== 'object' || Array.isArray(parsedInputs))
    ) {
      throw new Error('Invalid JSON');
    }
    if (
      parsedOutputs &&
      (typeof parsedOutputs !== 'object' || Array.isArray(parsedOutputs))
    ) {
      throw new Error('Invalid JSON');
    }
  } catch (e) {
    throw new Error('Invalid JSON');
  }

  return await fetcher<Response, never, RepoExampleCreateSchema>({
    url: example ? `${apiExamplesPath}/${example.id}` : `${apiExamplesPath}`,
    method: example ? 'PATCH' : 'POST',
    json: JSON.parse(payload),
    resolveWithResponse,
    headers: organizationId ? { 'X-Tenant-Id': organizationId } : undefined,
  });
};

export function useExampleCrud({
  example,
  datasetDataType,
  handleClose,
  onSuccess,
  onError,
}: {
  example?: ExampleSchema;
  datasetDataType: DatasetDataType;
  handleClose?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (payload: {
    dataset_id: string;
    inputs?: string;
    outputs?: string;
    metadata?: string;
    source_run_id?: string;
    use_source_run_attachments?: string[];
    split?: string[];
  }) => {
    setLoading(true);
    try {
      const res = await mutateExample(
        { organizationId, example, dataType: datasetDataType },
        payload,
        true
      );
      if (res.ok) {
        handleClose?.();
        onSuccess?.();
        const { dataset_id } = payload;
        if (example && example.dataset_id !== dataset_id) {
          navigate(
            `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${dataset_id}/example/${example.id}`
          );
        }
      } else {
        const body = await res.json();
        let detailString;
        if (typeof body?.detail === 'string') {
          detailString = body.detail;
        } else if (Array.isArray(body?.detail)) {
          const allDetailsAreStrings = body.detail.every(
            (item: unknown) => typeof item === 'string'
          );
          if (allDetailsAreStrings) {
            detailString = body.detail.join('\n');
          }
        } else if (body?.detail?.message) {
          detailString = body.detail.message;
        }
        if (!detailString) {
          detailString = 'Something went wrong.';
        }
        setError(detailString);
        onError?.(detailString);
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'Invalid JSON') {
        setError('Invalid JSON');
      } else if (e instanceof Error) {
        setError(e.message ?? 'Something went wrong.');
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    setError,
    onSubmit,
    loading,
    error,
  };
}

export function useEditorModeSelect({ value }: { value: JsonSchema7 }) {
  const isEditorAvailable = isSimpleParamsEditorAvailable(value);

  const [isEditor, setIsEditor] = useState(isEditorAvailable);
  const [language, setLanguage] = useLanguagePreference();

  const mode = isEditor && isEditorAvailable ? ('editor' as const) : language;

  return {
    mode,
    isEditor,
    setIsEditor,
    language,
    setLanguage,
    isEditorAvailable,
  };
}
